type ExtractionStep = {
  step?: number;
  step_total?: number;
  title?: string;
  url?: string;
  status?: string;
  main_contractors?: string[];
  involved_organizations?: string;
  routing_status?: string;
  error?: string;
  response_preview?: string;
  input_preview?: string;
  at?: string;
};

type Props = {
  log: Record<string, unknown>;
};

export function ExtractionLogDetail({ log }: Props) {
  const extraction = (log.extraction || {}) as Record<string, unknown>;
  const summary = (log.extraction_summary || {}) as Record<string, unknown>;
  const steps = (log.steps || []) as ExtractionStep[];
  const events = (log.events || []) as { at?: string; message?: string; level?: string }[];
  const errors = (log.errors || []) as { title?: string; error?: string }[];

  return (
    <div className="space-y-4">
      <div className="p-3 panel-border rounded bg-surface-container-low text-sm space-y-1">
        <p className="text-[10px] uppercase tracking-widest text-on-surface-variant">
          Phase 2 — Main contractor extraction
        </p>
        <p>
          Target: <strong>{String(extraction.target_company || "—")}</strong>
        </p>
        <p>
          Model: <code className="font-data-mono text-xs">{String(extraction.model || "—")}</code>
        </p>
        <p className="text-xs text-on-surface-variant font-data-mono">
          Job {String(summary.job_id || log.job_id || "—")} ·{" "}
          {String(extraction.pending_count ?? "—")} to process ·{" "}
          {String(extraction.skipped_existing ?? 0)} skipped (already done) ·{" "}
          {String(extraction.excluded_rejected ?? 0)} excluded (no target in text)
        </p>
        {summary.processed != null ? (
          <p className="text-xs">
            Result: {String(summary.processed)} processed · {String(summary.usable_count ?? 0)}{" "}
            with contractors · {String(summary.invalidated_count ?? 0)} invalidated
          </p>
        ) : null}
        {log.fatal_error ? (
          <p className="text-error text-xs mt-2">{String(log.fatal_error)}</p>
        ) : null}
      </div>

      {events.length > 0 ? (
        <div className="p-3 panel-border rounded">
          <p className="text-[10px] uppercase tracking-widest text-on-surface-variant mb-2">
            Run events
          </p>
          <ul className="text-xs font-data-mono space-y-1">
            {events.map((ev, i) => (
              <li key={i} className="text-on-surface-variant">
                <span className="text-outline">{ev.at?.slice(11, 19) || "—"}</span>{" "}
                {ev.message}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {steps.length === 0 ? (
        <p className="text-on-surface-variant text-sm">
          {log.status === "running"
            ? "Waiting for extraction steps… refresh or leave this page open."
            : "No per-page steps recorded."}
        </p>
      ) : (
        <div className="space-y-3">
          <p className="text-[10px] uppercase tracking-widest text-on-surface-variant">
            Per-page steps ({steps.length})
          </p>
          {steps.map((s, i) => {
            const st = String(s.status || "");
            const stClass =
              st === "error"
                ? "text-error"
                : st === "invalidated" || st === "skipped"
                  ? "text-tertiary"
                  : "text-secondary";
            const contractors = (s.main_contractors || []).join(", ") || "—";
            return (
              <div key={i} className="p-3 panel-border rounded bg-surface-container-lowest">
                <div className="flex justify-between gap-2 flex-wrap text-xs">
                  <span className="font-data-mono text-primary">
                    {s.step}/{s.step_total} · {s.title || "Untitled"}
                  </span>
                  <span className={stClass}>{st}</span>
                </div>
                {s.url ? (
                  <p className="text-[10px] text-on-surface-variant truncate mt-1">{s.url}</p>
                ) : null}
                <p className="text-xs mt-2">
                  Contractors: <strong>{contractors}</strong>
                </p>
                {s.involved_organizations ? (
                  <p className="text-xs text-on-surface-variant mt-1">
                    Involved: {s.involved_organizations}
                  </p>
                ) : null}
                {s.routing_status ? (
                  <p className="text-xs text-on-surface-variant mt-1">
                    Routing: {s.routing_status}
                  </p>
                ) : null}
                {s.error ? <p className="text-xs text-error mt-2">{s.error}</p> : null}
                {s.response_preview ? (
                  <pre className="text-[10px] font-data-mono mt-2 p-2 bg-surface-dim rounded max-h-40 overflow-auto">
                    {s.response_preview}
                  </pre>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      {errors.length > 0 ? (
        <div className="p-3 border border-error/30 rounded">
          <p className="text-[10px] uppercase text-error mb-2">Errors</p>
          <ul className="text-xs space-y-1">
            {errors.map((e, i) => (
              <li key={i}>
                {e.title}: {e.error}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
